import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import PubNub from 'pubnub';
import {
  FiArrowRight, FiClock, FiAlertCircle, FiSearch,
} from 'react-icons/fi';
import {
  C, Panel, PanelHeader, PanelTitle, PanelSubtitle, CountPill,
  TableWrap, Table, Empty, Badge, SearchInput,
} from '../chrome';
import {
  loadRoster, promoteAgentToTc, loadAudit, clearAudit,
} from '../../../actions/agentRoster';
import config from '../../../config';
import RoleChangeModal from './RoleChangeModal';
import AuditLogModal from './AuditLogModal';
import Pagination, { sliceForPage } from './Pagination';
import { formatTimestamp, toCsv, downloadCsv } from '../utils';

const PAGE_SIZE = 10;
const OPS_CHANNEL_BY_INSURER = {
  max:        'auto-dial-ops-max',
  starhealth: 'auto-dial-ops-starhealth',
};

// ─── Layout ────────────────────────────────────────────────────────────────
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.4rem;
  padding: 1.2rem 1.6rem;
  border-bottom: 1px solid ${C.border};
  background: ${C.surface};
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const ToolbarBtn = styled.button`
  background: ${C.surfaceAlt};
  border: 1px solid ${C.border};
  color: ${C.textSecondary};
  border-radius: 0.6rem;
  padding: 0.7rem 1.1rem;
  font-size: 1.15rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover:not(:disabled) {
    border-color: ${C.purple};
    color: ${C.textPrimary};
    background: ${C.purpleDim};
  }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const FilterChips = styled.div`
  display: inline-flex;
  background: ${C.surfaceAlt};
  border: 1px solid ${C.border};
  border-radius: 9999px;
  padding: 0.3rem;
  gap: 0.2rem;
`;

const Chip = styled.button`
  background: ${(p) => (p.active ? C.purple : 'transparent')};
  color: ${(p) => (p.active ? '#fff' : C.textMuted)};
  border: none;
  border-radius: 9999px;
  padding: 0.6rem 1.2rem;
  font-size: 1.15rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  &:hover { color: ${(p) => (p.active ? '#fff' : C.textPrimary)}; }
`;

const ChipCount = styled.span`
  background: ${(p) => (p.active ? 'rgba(255,255,255,0.2)' : C.surface)};
  color: inherit;
  border-radius: 9999px;
  padding: 0.1rem 0.55rem;
  font-size: 0.95rem;
  font-variant-numeric: tabular-nums;
`;

const SearchWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  & > svg {
    position: absolute;
    left: 1.1rem;
    color: ${C.textDim};
    pointer-events: none;
  }
  & > input {
    padding-left: 3.2rem;
  }
`;

// ─── Row bits ──────────────────────────────────────────────────────────────
const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Avatar = styled.div`
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  background: ${C.purpleSoft};
  border: 1px solid ${C.purple}33;
  color: ${C.purple};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const NameMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const NameMain = styled.div`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${C.textPrimary};
`;

const NameSub = styled.div`
  font-size: 1rem;
  color: ${C.textMuted};
  font-variant-numeric: tabular-nums;
`;

const StatusDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(p) => p.color || C.textMuted};
  display: inline-block;
  margin-right: 0.6rem;
`;

const ActionBtn = styled.button`
  background: ${C.surfaceAlt};
  border: 1px solid ${C.border};
  color: ${C.textSecondary};
  border-radius: 0.6rem;
  padding: 0.6rem 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.15s;
  position: relative;
  &:hover:not(:disabled) {
    border-color: ${C.purple};
    color: ${C.textPrimary};
    background: ${C.purpleDim};
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const LinkBtn = styled.button`
  background: transparent;
  border: none;
  color: ${C.textMuted};
  cursor: pointer;
  font-size: 1.05rem;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: ${C.borderLight};
  padding: 0.4rem 0;
  &:hover { color: ${C.purple}; text-decoration-color: ${C.purple}; }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  justify-content: flex-end;
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 0.6rem);
  right: 0;
  background: ${C.bg};
  border: 1px solid ${C.borderLight};
  border-radius: 0.5rem;
  padding: 0.6rem 0.9rem;
  font-size: 1.05rem;
  font-weight: 500;
  color: ${C.textSecondary};
  white-space: nowrap;
  z-index: 10;
  pointer-events: none;
  box-shadow: 0 8px 16px rgba(0,0,0,0.4);
`;

// ─── Helpers ───────────────────────────────────────────────────────────────
const initials = (name) => {
  if (!name) return '·';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('');
};

const liveStatus = (a) => {
  if (Number(a.is_on_auto_dial) === 1) return { label: 'On call', color: C.blue };
  if (Number(a.active) === 1)          return { label: 'Idle',    color: C.green };
  return { label: 'Offline', color: C.textMuted };
};

const BLOCKED_TOOLTIP = {
  ALREADY_TC:  'Already a tele-caller.',
  NOT_ADMIN:   'Only admin users (show_admin = 1) can be marked as tele-callers.',
  ON_CALL:     'On an active call — try again when the call ends.',
};

// ─── Component ─────────────────────────────────────────────────────────────
class AgentRosterPanel extends Component {
  state = {
    query:           '',
    page:            1,
    hoveredBlockId:  null,
    flipModal:       null,    // { agent } when open
    auditModal:      null,    // { agent } when open
  };

  componentDidMount() {
    this.props.loadRoster(this.props.agentTypeFromPage);
    this.setupPubNub();
  }

  componentDidUpdate(prev) {
    // Reload + reset page when the page-level pool toggle flips
    if (prev.agentTypeFromPage !== this.props.agentTypeFromPage) {
      this.props.loadRoster(this.props.agentTypeFromPage);
      this.setState({ page: 1, query: '' });
    }
  }

  componentWillUnmount() {
    if (this.pubnub) {
      try { this.pubnub.unsubscribeAll(); } catch (e) { /* noop */ }
      this.pubnub = null;
    }
  }

  // Subscribe to the ops channel but only react to role-change events. Dial
  // events fly past us — they affect the dashboard, not the roster's
  // membership / on-call columns. (We could also refresh on dial events to
  // pick up the on-call dot live; deferred — costs more API hits and the
  // value is marginal.)
  setupPubNub = () => {
    if (!config.pubnubPublishKey || !config.pubnubSubscribeKey) return;
    const channel = OPS_CHANNEL_BY_INSURER[this.props.userInsurer];
    if (!channel) return;
    try {
      this.pubnub = new PubNub({
        publishKey:   config.pubnubPublishKey,
        subscribeKey: config.pubnubSubscribeKey,
        uuid:         `auto-dial-ops-roster-${Date.now()}`,
      });
      this.pubnub.addListener({
        message: ({ message }) => {
          if (!message || !message.type) return;
          if (message.type === 'agent_role_changed') {
            this.props.loadRoster(this.props.agentTypeFromPage);
          }
        },
      });
      this.pubnub.subscribe({ channels: [channel] });
    } catch (e) {
      // Safe failure — the panel still works without push.
    }
  };

  setQuery = (e) => this.setState({ query: e.target.value, page: 1 });
  setPage  = (p) => this.setState({ page: p });

  exportCsv = () => {
    const rows = this.filteredAgents();
    if (!rows.length) return;
    // Slim each row to the columns ops actually want in a CSV
    // (drop the eligibility object — it's a UI concern, not a report concern).
    const csvRows = rows.map((a) => ({
      id:                a.id,
      name:              a.name || '',
      phone:             a.phone || '',
      role:              Number(a.is_tele_caller) === 1 ? 'tele_caller' : 'doctor',
      show_admin:        Number(a.show_admin) || 0,
      active:            Number(a.active) || 0,
      is_on_auto_dial:   Number(a.is_on_auto_dial) || 0,
      calls_today:       Number(a.calls_today) || 0,
      last_call_at:      a.last_call_at || '',
      eligible_to_mark_tc:
        a.eligibility && a.eligibility.can_promote_to_tc ? 1 : 0,
      blocked_by:
        a.eligibility && a.eligibility.blocked_by ? a.eligibility.blocked_by : '',
    }));
    const filename = `agent-roster-${this.props.agentTypeFromPage}-${Date.now()}.csv`;
    downloadCsv(filename, toCsv(csvRows));
  };

  openFlip = (agent) => this.setState({ flipModal: { agent } });
  closeFlip = () => this.setState({ flipModal: null });

  openAudit = (agent) => {
    this.setState({ auditModal: { agent } });
    this.props.loadAudit(agent.id, 20);
  };
  closeAudit = () => {
    this.setState({ auditModal: null });
    this.props.clearAudit();
  };

  doFlip = async (agentId, reason) => {
    const result = await this.props.promoteAgentToTc(agentId, reason);
    if (result.ok) {
      this.closeFlip();
    }
    return result;
  };

  filteredAgents = () => {
    const { agents } = this.props;
    const q = this.state.query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((a) =>
      [a.name, a.phone, String(a.id)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  };

  renderRow = (a) => {
    const { hoveredBlockId } = this.state;
    const { agentTypeFromPage } = this.props;
    const status = liveStatus(a);
    const showActionColumn = agentTypeFromPage === 'doctor';

    const elig    = a.eligibility || {};
    const blocked = !elig.can_promote_to_tc;
    const blockedReason = elig.blocked_by;
    // We do not show the action at all on TC rows (they're already TC).
    // For doctors who are already_tc (shouldn't happen in this filter) hide it too.
    const hideAction = !showActionColumn || blockedReason === 'ALREADY_TC';

    return (
      <tr key={a.id}>
        <td>
          <NameCell>
            <Avatar>{initials(a.name)}</Avatar>
            <NameMeta>
              <NameMain>{a.name || `Agent #${a.id}`}</NameMain>
              <NameSub>{a.phone || '—'}</NameSub>
            </NameMeta>
          </NameCell>
        </td>
        <td>
          <Badge color={status.color}>
            <StatusDot color={status.color} />
            {status.label}
          </Badge>
        </td>
        <td>
          {Number(a.show_admin) === 1
            ? <Badge color={C.purple}>Admin</Badge>
            : <Badge>Standard</Badge>}
        </td>
        <td>{Number(a.calls_today) || 0}</td>
        <td>{a.last_call_at ? formatTimestamp(a.last_call_at) : '—'}</td>
        <td>
          <Actions>
            <LinkBtn onClick={() => this.openAudit(a)}>History</LinkBtn>
            {!hideAction && (
              <ActionBtn
                onClick={() => !blocked && this.openFlip(a)}
                disabled={blocked}
                onMouseEnter={() => blocked && this.setState({ hoveredBlockId: a.id })}
                onMouseLeave={() => blocked && this.setState({ hoveredBlockId: null })}
              >
                Mark as TC <FiArrowRight size={12} />
                {blocked && hoveredBlockId === a.id && blockedReason && (
                  <Tooltip>{BLOCKED_TOOLTIP[blockedReason] || 'Not eligible'}</Tooltip>
                )}
              </ActionBtn>
            )}
          </Actions>
        </td>
      </tr>
    );
  };

  render() {
    const { agents, loading, error, agentTypeFromPage } = this.props;
    const { query, page, flipModal, auditModal } = this.state;
    const filtered = this.filteredAgents();
    const totalRows = filtered.length;
    const pageRows  = sliceForPage(filtered, page, PAGE_SIZE);
    const showActionColumn = agentTypeFromPage === 'doctor';

    const eligibleCount = (agents || []).filter(
      (a) => a.eligibility && a.eligibility.can_promote_to_tc
    ).length;

    const headerSub = agentTypeFromPage === 'tele_caller'
      ? 'Tele-callers in this pool'
      : `${eligibleCount} of ${agents.length} eligible to mark as tele-caller`;

    return (
      <Panel style={{ marginBottom: '2.4rem' }}>
        <PanelHeader>
          <div>
            <PanelTitle>Agent Roster</PanelTitle>
            <PanelSubtitle>{headerSub}</PanelSubtitle>
          </div>
          <CountPill color={C.purple}>{agents.length} agents</CountPill>
        </PanelHeader>

        <Toolbar>
          <FilterChips>
            <Chip
              active={agentTypeFromPage === 'doctor'}
              onClick={() => this.props.onPoolChange && this.props.onPoolChange('doctor')}
              type="button"
            >
              Doctor pool
              <ChipCount active={agentTypeFromPage === 'doctor'}>
                {agentTypeFromPage === 'doctor' ? agents.length : '·'}
              </ChipCount>
            </Chip>
            <Chip
              active={agentTypeFromPage === 'tele_caller'}
              onClick={() => this.props.onPoolChange && this.props.onPoolChange('tele_caller')}
              type="button"
            >
              Tele-caller pool
              <ChipCount active={agentTypeFromPage === 'tele_caller'}>
                {agentTypeFromPage === 'tele_caller' ? agents.length : '·'}
              </ChipCount>
            </Chip>
          </FilterChips>
          <ToolbarRight>
            <SearchWrap>
              <FiSearch size={14} />
              <SearchInput
                placeholder="Search name, phone…"
                value={query}
                onChange={this.setQuery}
              />
            </SearchWrap>
            <ToolbarBtn onClick={this.exportCsv} disabled={totalRows === 0}>
              Export CSV
            </ToolbarBtn>
          </ToolbarRight>
        </Toolbar>

        <TableWrap>
          {loading && agents.length === 0 ? (
            <Empty>
              <FiClock size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Loading roster…
            </Empty>
          ) : error ? (
            <Empty style={{ color: C.red }}>
              <FiAlertCircle size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
              {error}
            </Empty>
          ) : filtered.length === 0 ? (
            <Empty>
              {query
                ? `No agents match "${query}".`
                : agentTypeFromPage === 'tele_caller'
                  ? 'No tele-callers yet. Mark an admin doctor as a tele-caller to populate this pool.'
                  : 'No doctors in this pool.'}
            </Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Status</th>
                  <th>Type</th>
                  <th>Calls today</th>
                  <th>Last call</th>
                  <th style={{ textAlign: 'right' }}>{showActionColumn ? 'Actions' : 'History'}</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(this.renderRow)}
              </tbody>
            </Table>
          )}
        </TableWrap>

        {totalRows > 0 && (
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={totalRows}
            onChange={this.setPage}
          />
        )}

        <RoleChangeModal
          open={!!flipModal}
          agent={flipModal && flipModal.agent}
          onClose={this.closeFlip}
          onSubmit={this.doFlip}
        />

        <AuditLogModal
          open={!!auditModal}
          agentName={auditModal && auditModal.agent && auditModal.agent.name}
          audit={this.props.audit}
          loading={false}
          onClose={this.closeAudit}
        />
      </Panel>
    );
  }
}

const mapStateToProps = (state) => ({
  agents:      state.agentRoster.agents,
  loading:     state.agentRoster.loading,
  error:       state.agentRoster.error,
  audit:       state.agentRoster.audit,
  userInsurer: state.user.insurer,
});

const mapDispatchToProps = {
  loadRoster, promoteAgentToTc, loadAudit, clearAudit,
};

export default connect(mapStateToProps, mapDispatchToProps)(AgentRosterPanel);
